import type { ReactNode } from "react";

import { Trans } from "@lingui/react/macro";

import type { DataTableInstance } from "../table-features";
import type { FilterField } from "./types";

import { toggleableColumns } from "../column-visibility";
import { FilterDialogPopover } from "./filter-dialog-popover";
import { getActiveCount } from "./tanstack-bridge";

export function FilterToolbar<T>({
  table,
  fields,
  noun,
  actions,
  isLoading,
}: {
  table: DataTableInstance<T>;
  fields: FilterField<T>[];
  /** Noun for the auto-generated "X of Y" count text, pluralized for a count. */
  noun?: (count: number) => string;
  actions?: ReactNode;
  isLoading?: boolean;
}) {
  const activeCount = getActiveCount(table, fields);
  const totalCount = table.getCoreRowModel().rows.length;
  const shownCount =
    activeCount > 0 ? table.getFilteredRowModel().rows.length : totalCount;
  // Walked once here and handed to the dialog: it is both what decides whether
  // the button earns its place and what the Columns tab lists.
  const columns = toggleableColumns(table);
  // The dialog also hosts the Columns tab, so it earns its place as soon as
  // there is either something to filter or something to show/hide.
  const hasDialogContent = fields.length > 0 || columns.length > 0;

  return (
    <div className="flex flex-col gap-3 w-full flex-wrap sm:flex-row sm:items-center sm:justify-between sm:min-h-9">
      {actions && (
        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
          {actions}
        </div>
      )}

      <div className="flex items-center justify-between gap-3 sm:justify-end sm:shrink-0 sm:ml-auto">
        {noun && (
          <span className="text-sm text-muted-foreground whitespace-nowrap">
            {isLoading ? (
              <>
                {/* No count to agree with yet, so ask for the form a plain
                    plural takes ("patients", "pacientov"). */}
                <strong className="text-foreground">–</strong> {noun(0)}
              </>
            ) : activeCount > 0 ? (
              <Trans>
                <strong className="text-foreground">{shownCount}</strong> of{" "}
                <strong className="text-foreground">{totalCount}</strong>{" "}
                {noun(totalCount)}
              </Trans>
            ) : (
              <>
                <strong className="text-foreground">{totalCount}</strong>{" "}
                {noun(totalCount)}
              </>
            )}
          </span>
        )}
        {hasDialogContent && (
          <FilterDialogPopover
            table={table}
            fields={fields}
            columns={columns}
            disabled={isLoading}
          />
        )}
      </div>
    </div>
  );
}
